import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, Inject } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import {
  Attribute,
  AttributesCategory,
  AttributeTemplateData
} from '@shared/components/categories-attributes-selector/categories-attributes-selector.model';
import { SelectionPropertiesItem } from '@store/search-board/search-board.model';
import { DataElement, Template } from '@store/user/user.model';
import { CustomHeaderModalData } from '@store/custom-headers/custom-headers.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { select, Store } from '@ngrx/store';
import { tap } from 'rxjs/operators';
import { PremiumFieldCodes } from '@root/search/models/display-fields.constant';
import { selectIsPremiumViewAllowed } from '@store/search-board/search-board.selector';
import { AppState } from '@root/app-state.module';

@UntilDestroy()
@Component({
  selector: 'rlst-customize-search-result-view',
  templateUrl: './customize-search-result-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomizeSearchResultViewComponent implements OnInit {

  public categories: Array<AttributesCategory>;
  public selectedCategory: AttributesCategory = null;

  public allAttributes: Array<Attribute>;
  public displayedAttributes: Array<Attribute>;
  public selectedAttributes: Array<SelectionPropertiesItem> = [];

  public isMobileNextStep: boolean = false;
  public maxNumberAttributesSelection: number;

  private readonly attributeTemplateData: Array<AttributeTemplateData> = [];

  constructor(private readonly store$: Store<AppState>,
              private readonly cdr: ChangeDetectorRef,
              private readonly dialogRef: MatDialogRef<any>,
              @Inject(MAT_DIALOG_DATA) public data: CustomHeaderModalData) {
  }

  ngOnInit(): void {
    const {headers, preference} = this.data;
    this.maxNumberAttributesSelection = this.data.maxItemNumber;

    this.store$.pipe(
      select(selectIsPremiumViewAllowed),
      untilDestroyed(this),
      tap((isPremiumViewAllowed) => {
        this.mapData(headers, preference, isPremiumViewAllowed);

        this.cdr.markForCheck();
      })
    ).subscribe();
  }

  private mapData(currentGridHeaders: Array<DataElement>, tablePreference: Template, isPremiumViewAllowed: boolean) {
    const categories: Array<AttributesCategory> = [];
    const allAttributes: Array<Attribute> = [];

    tablePreference.childTemplateList.forEach((gridHeaderElement: Template) => {

      if (gridHeaderElement.dataElementList.length) {
        categories.push(({
          name: gridHeaderElement.description,
          id: gridHeaderElement.templateCode,
        }));
      }

      gridHeaderElement.dataElementList.forEach((dataElement: DataElement) => {
        const isSelected: boolean = currentGridHeaders.some((g: DataElement) => g.fieldCode === dataElement.fieldCode);
        const isPremium = !!PremiumFieldCodes.find((i) => i === dataElement.fieldCode);
        allAttributes.push({
          label: dataElement.label,
          name: gridHeaderElement.templateCode,
          fieldCode: dataElement.fieldCode,
          selected: isSelected,
          disabled: !isPremiumViewAllowed && isPremium
        });

        this.attributeTemplateData.push({label: dataElement.label, data: dataElement});
      });
    });

    this.selectedAttributes = currentGridHeaders.reduce((selected: Array<SelectionPropertiesItem>, header: DataElement) => {
      const selectedAttr = allAttributes.find((attribute: Attribute) => attribute.fieldCode === header.fieldCode);
      if (selectedAttr) {
        selected.push({
          id: selectedAttr.name,
          fieldName: selectedAttr.label,
          tooltip: selectedAttr.label,
          fieldCode: selectedAttr.fieldCode
        });
      }
      return selected;
    }, []);

    this.categories = categories;
    this.allAttributes = allAttributes;
    this.displayedAttributes = allAttributes;
  }

  public selectedCategoryFn(category: AttributesCategory): void {
    this.selectedCategory = category;
    if (category) {
      this.displayedAttributes = this.allAttributes.filter((attribute: Attribute) => attribute.name === category.id);
    } else {
      this.displayedAttributes = [...this.allAttributes];
    }
  }

  public selectAttribute(attr: Attribute): void {
    const selectedAttr: Attribute = this.allAttributes.find((a: Attribute) => a.fieldCode === attr.fieldCode);

    if (selectedAttr) {
      selectedAttr.selected = attr.selected;
    }

    if (attr.selected) {
      this.selectedAttributes = [
        ...this.selectedAttributes,
        {id: attr.name, fieldName: attr.label, tooltip: attr.label, fieldCode: attr.fieldCode}
      ];
    } else {
      this.selectedAttributes = this.selectedAttributes
        .filter((selectedProperty: SelectionPropertiesItem) => selectedProperty.fieldCode !== attr.fieldCode);
    }
  }

  public removeRegion(attr: SelectionPropertiesItem): void {
    const selectedAttr = this.displayedAttributes.find((a: Attribute) => a.fieldCode === attr.fieldCode);
    if (selectedAttr) {
      selectedAttr.selected = false;
    }

    this.selectedAttributes = this.selectedAttributes
      .filter((selectedProperty: SelectionPropertiesItem) => selectedProperty.fieldCode !== attr.fieldCode);
    this.displayedAttributes = [...this.displayedAttributes];
  }

  public clearSelection(): void {
    this.selectedAttributes.forEach((selectedAttr: SelectionPropertiesItem) => {
      const attribute = this.allAttributes.find((attr: Attribute) => attr.fieldCode === selectedAttr.fieldCode);
      if (attribute) {
        attribute.selected = false;
      }
    });
    this.selectedAttributes = [];
    this.displayedAttributes = [...this.displayedAttributes];
  }

  public apply(): void {
    const newHeaders: Array<DataElement> = [];

    this.selectedAttributes.forEach((selectedAttr: SelectionPropertiesItem) => {
      const header = this.attributeTemplateData
        .find((attributeTemplate: AttributeTemplateData) => {
          return attributeTemplate.data.fieldCode === selectedAttr.fieldCode;
        });
      if (header) {
        newHeaders.push(header.data);
      }
    });
    this.dialogRef.close(newHeaders);
  }

  public toggleMobileNextStep(): void {
    this.isMobileNextStep = !this.isMobileNextStep;
  }

  public close(): void {
    this.dialogRef.close();
    this.isMobileNextStep = false;
  }
}
